import { useEffect, useState } from 'react'
import { PlayCircle } from 'lucide-react'
import AdminDialog from '@/components/AdminDialog'
import { productionCommissionsApi } from '@/api/productionCommissions'
import { errorMessage } from '@/api/client'
import {
  applicationLabels,
  type ApplicantVideo,
  type ApplicationStatus,
  type ProductionCommission,
} from './productionCommissionsModel'

function VideoCard({ video, applicationId, kind, index }: { video: ApplicantVideo; applicationId: string; kind: 'works' | 'trial'; index: number }) {
  const [source, setSource] = useState('')
  useEffect(() => {
    if (!video.url) return
    let active = true
    let objectUrl = ''
    void productionCommissionsApi.media(applicationId, kind, index).then(url => { objectUrl = url; if (active) setSource(url); else URL.revokeObjectURL(url) }).catch(() => {})
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [applicationId, kind, index, video.url])
  return (
    <div className="w-32 shrink-0">
      {source ? (
        <video controls preload="metadata" src={source} aria-label={video.title} className="aspect-video w-full rounded-lg bg-slate-900" />
      ) : (
        <div className="flex aspect-video items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-slate-400" title="示例视频尚无可播放文件">
          <PlayCircle size={30} aria-hidden="true" />
        </div>
      )}
      <div className="mt-1 flex justify-between gap-1 text-xs text-slate-500"><span className="truncate">{video.title}</span><span>{video.duration}</span></div>
    </div>
  )
}

export default function ProductionCommissionApplications({
  offer,
  onClose,
  onReview,
  onWinner,
  readOnly = false,
  embedded = false,
}: {
  offer: ProductionCommission
  onClose: () => void
  onReview: (ids: string[], status: 'approved' | 'rejected', reason: string, scores: Record<string, number>) => Promise<void> | void
  onWinner?: (applicationId: string) => Promise<void> | void
  readOnly?: boolean
  embedded?: boolean
}) {
  const [tab, setTab] = useState<ApplicationStatus | 'all'>('all')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [activeId, setActiveId] = useState<string>()
  const [reason, setReason] = useState('')
  const [scores, setScores] = useState<Record<string, number>>({})
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const items = tab === 'all' ? offer.applications : offer.applications.filter((item) => item.status === tab)
  const selectable = items.filter(item => item.status === 'pending')
  const active = offer.applications.find((item) => item.id === activeId) || items[0]
  const allSelected = selectable.length > 0 && selectable.every((item) => selectedIds.includes(item.id))

  function switchTab(next: ApplicationStatus | 'all') {
    setTab(next)
    setSelectedIds([])
    setActiveId(undefined)
    setReason('')
    setError('')
  }

  async function review(status: 'approved' | 'rejected') {
    if (!selectedIds.length) {
      setError('请先选择需要审核的申请')
      return
    }
    if (status === 'rejected' && !reason.trim()) {
      setError('拒绝申请时请填写原因')
      return
    }
    if (status === 'approved' && selectedIds.some(id => !Number.isInteger(scores[id]) || scores[id] < 0 || scores[id] > 100)) {
      setError('请给每位通过审核的申请填写 0～100 分')
      return
    }
    setBusy(true)
    try {
      await onReview(selectedIds, status, reason.trim(), scores)
      setSelectedIds([])
      setActiveId(undefined)
      setReason('')
      setError('')
    } catch (cause) { setError(errorMessage(cause)) }
    finally { setBusy(false) }
  }

  const content = (
    <>
      <div role="tablist" aria-label="申请状态" className="mb-4 flex gap-2 border-b border-slate-200">
        {([['all', '全部'], ...Object.entries(applicationLabels)] as [ApplicationStatus | 'all', string][]).map(([status, label]) => (
          <button key={status} type="button" role="tab" aria-selected={tab === status} onClick={() => switchTab(status)}
            className={'border-b-2 px-3 py-2 text-sm ' + (tab === status ? 'border-brand-600 font-medium text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-900')}>
            {label} <span className="ml-1 text-xs">{status === 'all' ? offer.applications.length : offer.applications.filter((item) => item.status === status).length}</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead className="bg-slate-100 text-slate-600"><tr>
            <th className="w-10 p-3"><input type="checkbox" aria-label="全选待审核申请" checked={allSelected} disabled={readOnly || !selectable.length} onChange={(event) => setSelectedIds(event.target.checked ? selectable.map((item) => item.id) : [])} /></th>
            <th className="p-3">申请人</th><th className="p-3">申请时间</th><th className="p-3">作品/经验</th><th className="p-3">状态</th><th className="p-3">评分</th>
          </tr></thead>
          <tbody>{items.map((item) => (
            <tr key={item.id} className={'border-t border-slate-100 ' + (active?.id === item.id ? 'bg-brand-50' : '')}>
              <td className="p-3"><input type="checkbox" aria-label={`选择 ${item.name}`} checked={selectedIds.includes(item.id)} disabled={readOnly || item.status !== 'pending'} onChange={(event) => setSelectedIds((current) => event.target.checked ? [...current, item.id] : current.filter((id) => id !== item.id))} /></td>
              <td className="p-3"><button type="button" className="font-medium text-brand-700 hover:underline" onClick={() => setActiveId(item.id)}>{item.name}</button></td>
              <td className="whitespace-nowrap p-3 text-slate-600">{item.appliedAt}</td>
              <td className="p-3 text-slate-600">{item.experience}</td>
              <td className="p-3"><span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">{applicationLabels[item.status]}</span></td>
              <td className="p-3">{item.status === 'pending' && !readOnly ? <input aria-label={`给 ${item.name} 评分`} type="number" min={0} max={100} step={1} className="w-20 rounded border border-slate-300 p-1" value={scores[item.id] ?? ''} onChange={event => setScores(current => { const next = { ...current }; if (event.target.value === '') delete next[item.id]; else next[item.id] = Number(event.target.value); return next })} /> : item.score ?? '—'}</td>
            </tr>
          ))}</tbody>
        </table>
        {!items.length && <p className="p-8 text-center text-sm text-slate-500">暂无{tab === 'all' ? '' : applicationLabels[tab]}申请</p>}
      </div>
      {active && <section className="mt-4 rounded-xl border border-slate-200 p-4">
        <h3 className="mb-3 border-b border-slate-100 pb-2 text-sm font-semibold">申请人详情</h3>
        <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[92px_1fr]">
          <dt className="text-slate-500">申请人</dt><dd>{active.name}</dd>
          <dt className="text-slate-500">联系方式</dt><dd className="space-y-1"><p>邮箱：{active.email || '—'}</p><p>手机号：{active.phone || '—'}</p><p>微信号：{active.wechat || '—'}</p></dd>
          <dt className="text-slate-500">作品视频</dt><dd className="flex flex-wrap gap-3">{active.works.length ? active.works.slice(0, 5).map((video, index) => <VideoCard key={index} video={video} applicationId={active.id} kind="works" index={index} />) : '暂无作品视频'}</dd>
          <dt className="text-slate-500">试稿作品视频</dt><dd>{active.trialWork ? <VideoCard video={active.trialWork} applicationId={active.id} kind="trial" index={0} /> : '暂无试稿作品视频'}</dd>
          <dt className="text-slate-500">申请时间</dt><dd>{active.appliedAt}</dd>
          <dt className="text-slate-500">申请时报价</dt><dd>{active.quotedRateYuanPerMinute ?? 1000}元/分钟</dd>
          <dt className="text-slate-500">评分</dt><dd>{active.score ?? '待评分'}{active.isWinner ? ' · 中标者' : ''}</dd>
          {active.rejectionReason && <><dt className="text-slate-500">拒绝原因</dt><dd>{active.rejectionReason}</dd></>}
        </dl>
      </section>}
      {(tab === 'pending' || tab === 'all') && !readOnly && <div className="mt-4 space-y-2">
        <label htmlFor="application-rejection-reason" className="text-sm font-medium">拒绝原因</label>
        <textarea id="application-rejection-reason" className="min-h-20 w-full rounded-lg border border-slate-200 p-3 text-sm" maxLength={200} placeholder="拒绝时需填写原因…" value={reason} onChange={(event) => { setReason(event.target.value); setError('') }} />
        <p className="text-right text-xs text-slate-400">{reason.length}/200</p>
      </div>}
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      {(tab === 'pending' || tab === 'all') && !readOnly && <div className="mt-4 flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-4">
        <button type="button" onClick={() => void review('rejected')} disabled={busy || !selectedIds.length} className="rounded-lg border border-slate-300 px-4 py-2 text-sm disabled:opacity-50">拒绝申请</button>
        <button type="button" onClick={() => void review('approved')} disabled={busy || !selectedIds.length} className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-50">通过申请</button>
      </div>}
      {tab === 'approved' && !readOnly && !offer.winnerApplicationId && active && onWinner && <div className="mt-4 flex justify-end border-t border-slate-200 pt-4"><button type="button" disabled={busy || offer.applications.some(item => item.status === 'pending')} onClick={async () => { setBusy(true); try { await onWinner(active.id) } catch (cause) { setError(errorMessage(cause)) } finally { setBusy(false) } }} className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-50">选为中标者</button></div>}
    </>
  )
  return embedded ? content : <AdminDialog title={`申请审核 · ${offer.title}`} onClose={onClose} busy={busy} wide>{content}</AdminDialog>
}
